import { useState, useEffect, useCallback } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { clearInteractiveLoginAttempt } from "../auth/acquireIdToken";
import type { User } from "@domaddress/shared";
import { DOMINICUS_LOGO_URL } from "@domaddress/shared";

const MOBILE_NAV_MQ = "(max-width: 768px)";

interface Props {
  user: User | null;
}

export default function Layout({ user }: Props) {
  const { instance } = useMsal();
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [isNarrowViewport, setIsNarrowViewport] = useState(false);

  const closeMobileNav = useCallback(() => setMobileNavOpen(false), []);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia(MOBILE_NAV_MQ);
    const sync = () => setIsNarrowViewport(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    closeMobileNav();
  }, [location.pathname, closeMobileNav]);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia(MOBILE_NAV_MQ);
    const onChange = () => {
      if (!mq.matches) closeMobileNav();
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [closeMobileNav]);

  useEffect(() => {
    if (typeof document === "undefined" || !mobileNavOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeMobileNav();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [mobileNavOpen, closeMobileNav]);

  const handleLogout = () => {
    localStorage.removeItem("access_token");
    clearInteractiveLoginAttempt();
    void instance.logoutRedirect();
  };

  const navClass = ({ isActive }: { isActive: boolean }) => `nav-item ${isActive ? "active" : ""}`;

  return (
    <div className={`layout${mobileNavOpen ? " layout--nav-open" : ""}`}>
      <header className="layout-mobile-header">
        <button
          type="button"
          className="layout-mobile-menu-btn"
          onClick={() => setMobileNavOpen((o) => !o)}
          aria-expanded={mobileNavOpen}
          aria-controls="agenda-sidebar-nav"
          aria-label={mobileNavOpen ? "Menu sluiten" : "Menu openen"}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            {mobileNavOpen ? (
              <>
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </>
            ) : (
              <>
                <line x1="4" y1="6" x2="20" y2="6" />
                <line x1="4" y1="12" x2="20" y2="12" />
                <line x1="4" y1="18" x2="20" y2="18" />
              </>
            )}
          </svg>
        </button>
        <div className="layout-mobile-header-brand">
          <img className="layout-mobile-header-logo" src={DOMINICUS_LOGO_URL} alt="" width={40} height={40} decoding="async" />
          <span className="layout-mobile-header-title">Agenda</span>
        </div>
      </header>

      {mobileNavOpen && (
        <div className="sidebar-backdrop" role="presentation" aria-hidden onClick={closeMobileNav} />
      )}

      <aside
        className="sidebar"
        id="agenda-sidebar"
        aria-label="Hoofdnavigatie"
        inert={isNarrowViewport && !mobileNavOpen ? true : undefined}
      >
        <div className="sidebar-logo">
          <img className="sidebar-logo-mark" src={DOMINICUS_LOGO_URL} alt="Dominicus Amsterdam" width={132} height={40} decoding="async" />
          <div className="sidebar-logo-text">
            <span className="sidebar-title">Agenda</span>
          </div>
        </div>

        <nav className="sidebar-nav" id="agenda-sidebar-nav">
          <NavLink to="/kalender" className={navClass} onClick={closeMobileNav}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            Kalender
          </NavLink>
          <NavLink to="/" end className={navClass} onClick={closeMobileNav}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 9.5 12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z" />
              <path d="M9 21V12h6v9" />
            </svg>
            Overzicht
          </NavLink>
          <NavLink to="/evenementen" className={navClass} onClick={closeMobileNav}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            Evenementen
          </NavLink>
          <NavLink to="/rooster-sjablonen" className={navClass} onClick={closeMobileNav}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
            </svg>
            Rooster-sjablonen
          </NavLink>
          <NavLink to="/roosters" className={navClass} onClick={closeMobileNav}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
            Roosters
          </NavLink>
        </nav>

        <div className="sidebar-user">
          {user && (
            <>
              <div className="user-avatar">
                {user.avatar_url ? (
                  <img src={user.avatar_url} alt={user.name} />
                ) : (
                  <span>{user.name.split(" ").map((n) => n[0]).join("").slice(0, 2)}</span>
                )}
              </div>
              <div className="user-info">
                <span className="user-name">{user.name}</span>
                <span className="user-email">{user.email}</span>
              </div>
            </>
          )}
          <button
            type="button"
            className="btn-logout"
            onClick={() => {
              closeMobileNav();
              handleLogout();
            }}
            title="Afmelden"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
          </button>
        </div>
      </aside>

      <main className="main-content">
        <Outlet />
      </main>
    </div>
  );
}
